import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, message, Typography, Select } from 'antd';
import {
	downloadLocationsCSVForBuyer,
	fetchLocationByIdForBuyer,
	locationsRestCrudThunksForBuyer,
} from '../../thunks/locations_thunks';
import { updateFiltersEntities } from '../../thunks/thunks_wrapper';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { EmptyState } from '../empty_state/EmptyState';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	getTupleNameFieldName,
	getTupleValueFieldName,
} from '../../utils/DataAccessUtils';
import { EmptorTag } from '../emptor_tag/EmptorTag';
import { LOCATIONS_CRUD_ACTION_CREATORS } from '../../actions/locations_actions';
import { BUYER_ONLY_NO_STORE_ASSOCIATE } from '../roles/roles';
import { buyerRoleUpdateUser } from '../../thunks/buyer_contacts_thunks';
import BuyerRoleUpdateForm from '../buyer_role_update_form/BuyerRoleUpdateForm';
import { FILTER_CONFIG_NAMES, FILTER_FIELD_TYPE, ROLE_TYPES } from '../../utils/DataConstants';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import OWAsyncTupleSelect from '../ow_async_select/OWAsyncTupleSelect';
import { locationTypesAdditionalFieldsRestCrudThunksForBuyer } from '../../thunks/location_types_additional_fields_thunks';
import { INITIAL_IS_ACTIVE_FILTERS } from '../../utils/FilterUtils';
import { brandsRestCrudThunksForBuyer } from '../../thunks/brands_thunks';
import LocationsReduxDataTable from '../locations_redux_data_table/LocationsReduxDataTable';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import OWButtonWithSupplierManagedLock from '../common/OWButtonWithSupplierManagedLock';
import { DownloadOutlined } from '@ant-design/icons';
import BrandLogoDisplay from '../brands_overview_index_page/BrandLogoDisplay';
import { useFilters } from '../../hooks/useFilters';
import {
	getLocationFilterConfig,
	syncLocationsOverviewQueryParams,
} from '../../utils/LocationFilterUtils';
import {
	buildLocationsMapPopupParts,
	getLocationContactGroups,
} from '../../utils/LocationsMapPopupUtils';
import LocationsViewModeToggle, {
	LocationsViewMode,
} from '../locations_view_mode_toggle/LocationsViewModeToggle';
import LocationsMapView from '../locations_map_view/LocationsMapView';
import LocationsOverviewMapToolbar from '../locations_overview_map_toolbar/LocationsOverviewMapToolbar';
import useMountEffect from '../../hooks/useMountEffect';
require('./LocationsIndexPage.less');
const { Text } = Typography;

const TC_NAME = 'locationsIndex';
const LOCATIONS_OVERVIEW_TARGET_COLLECTION = 'locationsIndex';
const LOCATIONS_ADVANCED_FILTERS = 'locationsAdvancedFilters';

const LocationsIndexPage: FC<any> = ({
	history,
	location,
	locations,
	setLocationFiltersInStore,
	refetchLocationsWithFilters,
	clearAndUpdateFilters,
	currentUser,
	roleUpdateUser,
	exportLocations,
	fetchLocationTypesAdditionalFields,
	fetchMultipleLocationTypesAdditionalFields,
	locationTypesAdditionalFields,
	fetchBrandsCount,
	fetchFilteredLocationsLite,
	fetchLocationDetails,
}) => {
	const [exporting, setExporting] = useState(false);
	const [viewMode, setViewMode] = useState<LocationsViewMode>('list');
	const [inlineMapLoading, setInlineMapLoading] = useState(false);
	const [inlineMapLocations, setInlineMapLocations] = useState<any[]>([]);
	const [mapFullscreenOpen, setMapFullscreenOpen] = useState(false);

	const longitudeForMap = useCallback((el) => nullSafeGet('buyerFacility.longitude', el), []);
	const latitudeForMap = useCallback((el) => nullSafeGet('buyerFacility.latitude', el), []);
	const [roleUpdateVisible, setRoleUpdateVisible] = useState(false);
	const [userContact, setUserContact] = useState<any>(null);
	const formRefs = React.useRef<any>({ roleUpdateForm: null });

	const [brandAvailable, setBrandAvailable] = useState(false);

	const activeFilters = useMemo(
		() => nullSafeGetOrElse(`${TC_NAME}.filters`, locations, {}),
		[locations]
	);

	// Holds the live merged filter state for map mode. `activeFilters` is memoized off the
	// `locations` prop, so within a single render tick it is the pre-render snapshot. When
	// FilterOptionsWrapper switches an operator it fires handleChange immediately followed by
	// updateFilters({}); both run before a re-render, so merging the second call against the
	// stale `activeFilters` would re-issue the map request with the old filter values. Merging
	// against this ref instead keeps the follow-up call in sync with what was just applied.
	// Synced to the latest committed filters on every render (no effect needed).
	const latestMapFiltersRef = useRef(activeFilters);
	latestMapFiltersRef.current = activeFilters;

	const loadInlineMapLocations = useCallback(
		(filters) => {
			setInlineMapLoading(true);
			return fetchFilteredLocationsLite(filters)
				.then((data) => {
					setInlineMapLocations(Array.isArray(data) ? data : []);
				})
				.catch((err) => {
					message.error(typeof err === 'string' ? err : 'Failed to load locations');
					setViewMode('list');
					setMapFullscreenOpen(false);
				})
				.finally(() => setInlineMapLoading(false));
		},
		[fetchFilteredLocationsLite]
	);

	const applyLocationFilters = useCallback(
		(filters) => {
			setLocationFiltersInStore(filters);
			if (viewMode === 'list') {
				refetchLocationsWithFilters(filters);
			} else if (viewMode === 'map') {
				const mergedFilters = { ...latestMapFiltersRef.current, ...filters };
				latestMapFiltersRef.current = mergedFilters;
				syncLocationsOverviewQueryParams(history, locations, mergedFilters, TC_NAME);
				loadInlineMapLocations(mergedFilters);
			}
		},
		[
			setLocationFiltersInStore,
			refetchLocationsWithFilters,
			loadInlineMapLocations,
			viewMode,
			history,
			locations,
		]
	);

	const applyClearAndUpdateFilters = useCallback(
		(filters, targetCollectionName) => {
			clearAndUpdateFilters(filters, targetCollectionName);
			if (viewMode === 'list') {
				refetchLocationsWithFilters({});
			} else if (viewMode === 'map') {
				latestMapFiltersRef.current = filters;
				syncLocationsOverviewQueryParams(history, locations, filters, TC_NAME);
				loadInlineMapLocations(filters);
			}
		},
		[
			clearAndUpdateFilters,
			viewMode,
			refetchLocationsWithFilters,
			loadInlineMapLocations,
			history,
			locations,
		]
	);

	useMountEffect(() => {
		fetchBrandsCount()
			.then((count) => setBrandAvailable(count > 0))
			.catch(() => {});
	});

	const locationFilters = useFilters({
		filterTargetCollectionName: LOCATIONS_ADVANCED_FILTERS,
		overviewCollectionName: LOCATIONS_OVERVIEW_TARGET_COLLECTION,
		updateFilters: applyLocationFilters,
	});

	const additionalFieldsFilter = useMemo(
		() => ({
			label: 'Additional Fields',
			fieldName: FILTER_CONFIG_NAMES.ADDITIONAL_FIELD,
			type: FILTER_FIELD_TYPE.TUPLE,
			stateSlice: locationTypesAdditionalFields,
			filtersValueAccessor: (filtersStateSlice) => {
				const name = filtersStateSlice[getTupleNameFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)];
				const value =
					filtersStateSlice[getTupleValueFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)];
				return [name, value];
			},
			handleChange: (values) => {
				applyLocationFilters({
					[getTupleNameFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)]: nullSafeGetOrElse(
						'0',
						values,
						undefined
					),
					[getTupleValueFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)]: nullSafeGetOrElse(
						'1',
						values,
						undefined
					),
				});
			},
			targetCollectionName: LOCATIONS_ADVANCED_FILTERS,
			fetch: fetchLocationTypesAdditionalFields,
			fetchMultiple: fetchMultipleLocationTypesAdditionalFields,
			renderRecord: (entity) => (
				<Select.Option key={entity.id} value={entity.id}>
					<div className="flex flex-row items-center">
						<div>{entity.name}</div>
					</div>
				</Select.Option>
			),
			valueAccessor: (item) => item.name,
			component: OWAsyncTupleSelect,
		}),
		[
			locationTypesAdditionalFields,
			applyLocationFilters,
			fetchLocationTypesAdditionalFields,
			fetchMultipleLocationTypesAdditionalFields,
		]
	);

	const filterConfig = useMemo(
		() =>
			getLocationFilterConfig({
				filters: locationFilters,
				brandAvailable,
				additionalFieldsFilter,
			}),
		[locationFilters, brandAvailable, additionalFieldsFilter]
	);

	const saveFormRef = useCallback(
		(formName) => (formRef) => {
			formRefs.current[formName] = formRef;
		},
		[]
	);

	const showRoleUpdateModal = useCallback(
		(record) => (e) => {
			setRoleUpdateVisible(true);
			setUserContact({
				contact: { ...record },
				role: record['roles'],
				locationIds: record['locationIds'] || [],
			});
		},
		[]
	);

	const handleBuyerRoleUpdateSubmit = useCallback(() => {
		const form = formRefs.current['roleUpdateForm'].props.form;
		form.validateFields((err, values) => {
			if (err) return;
			const updateRole = {
				userId: values.email,
				roles: [values.roles],
				locationIds: values.locationIds,
			};
			roleUpdateUser(updateRole).then(() => {
				setRoleUpdateVisible(false);
				form.resetFields();
				message.success('Location Access Granted');
				history.push('/buyer/locations/overview/all');
			});
		});
	}, [roleUpdateUser, history]);

	const handleBuyerRoleUpdateCancel = useCallback(() => {
		setRoleUpdateVisible(false);
	}, []);

	const downloadCSV = useCallback(() => {
		const filters = nullSafeGetOrElse(`${TC_NAME}.filters`, locations, {});
		setExporting(true);
		exportLocations(filters)
			.catch((err) => message.error(err))
			.finally(() => setExporting(false));
	}, [locations, exportLocations]);

	const popupRenderFunc = useCallback(
		(record) => buildLocationsMapPopupParts(record, ROLE_TYPES.BUYER),
		[]
	);

	// Buyers see primary + facilities contacts on each location (no technician line — that is the
	// internal-tech view). Shared with the tooltip via getLocationContactGroups.
	const mapContactGroupsAccessor = useCallback((record) => getLocationContactGroups(record), []);

	const fetchLocationDetailsForMap = useCallback(
		(entity) => fetchLocationDetails(nullSafeGet('id', entity)),
		[fetchLocationDetails]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				defaultSortOrder: 'ascend',
				render: (text, record) => (
					<div className="locationsIndexPage__row">
						<div>
							<div className="flex items-center">
								<BrandLogoDisplay brand={nullSafeGet('brand', record)} />
								<div>
									<div className="locationsIndexPage__rowTitle">
										<Ellipsis tooltip={true} length={64}>
											{record.name}
										</Ellipsis>
									</div>
									<div className="locationsIndexPage__rowSubtitle">
										<div className="locationsIndexPage__rowInlineGroup">
											{nullSafeGet('buyerFacility.primaryAddress.city', record)}
										</div>
										<div className="locationsIndexPage__rowInlineGroup2">
											{nullSafeGet('locationType.name', record)} {nullSafeGet('storeId', record)}
										</div>
										{record.isActive ? null : (
											<div className="locationsIndexPage__rowInlineGroup">
												<Text className={'mr-1'} type={'danger'}>
													Inactive
												</Text>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
						<div className="flex items-end">
							<EmptorTag className="locationsIndexPage__pill">
								{record.totalNotClosedWOCount > 0
									? record.totalNotClosedWOCount + ' Open WOs'
									: 'No Open WOs'}
							</EmptorTag>
						</div>
					</div>
				),
			},
		],
		[]
	);

	const detailPathForRecord = useCallback(
		(record) => {
			const path = `/buyer/locations/detail/${nullSafeGet(
				'id',
				record
			)}/details?targetCollectionName=${LOCATIONS_OVERVIEW_TARGET_COLLECTION}`;
			return getLinkWIthBackLinkParams(location, 'Back to Assets', path, {
				preserve: true,
			});
		},
		[location]
	);

	const onRow = useCallback(
		(record) => ({
			onClick: () => history.push(detailPathForRecord(record)),
		}),
		[history, detailPathForRecord]
	);

	const handleViewModeChange = useCallback(
		(nextViewMode: LocationsViewMode) => {
			if (nextViewMode === viewMode) {
				return;
			}
			setViewMode(nextViewMode);
			if (nextViewMode === 'list') {
				setInlineMapLocations([]);
				setMapFullscreenOpen(false);
				refetchLocationsWithFilters({});
			} else {
				loadInlineMapLocations(activeFilters);
			}
		},
		[viewMode, refetchLocationsWithFilters, loadInlineMapLocations, activeFilters]
	);

	const exportCsvButton = useMemo(
		() => (
			<Button
				size="large"
				icon={<DownloadOutlined />}
				className="inline-block-visible-md"
				loading={exporting}
				onClick={downloadCSV}
			>
				<span className="inline-block-visible-xl">Export CSV</span>
			</Button>
		),
		[downloadCSV, exporting]
	);

	const addLocationButton = useMemo(
		() =>
			currentUser.roles.some((role) => BUYER_ONLY_NO_STORE_ASSOCIATE.has(role)) ? (
				<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_LOCATIONS}>
					<OWButtonWithSupplierManagedLock
						size="large"
						type="primary"
						onClick={() => history.push('/buyer/locations/overview/new')}
					>
						Add Location
					</OWButtonWithSupplierManagedLock>
				</AccessPermissionChecker>
			) : null,
		[currentUser.roles, history]
	);

	const locationsTableRightActions = useMemo(
		() => (
			<div className="flex items-center gap-2">
				{exportCsvButton}
				<LocationsViewModeToggle
					viewMode={viewMode}
					onViewModeChange={handleViewModeChange}
					isSupplier={false}
				/>
				{addLocationButton}
			</div>
		),
		[exportCsvButton, addLocationButton, viewMode, handleViewModeChange]
	);

	const locationsFullscreenToolbar = useMemo(
		() => (
			<LocationsOverviewMapToolbar
				filterConfig={filterConfig}
				updateFilters={applyLocationFilters}
				clearAndUpdateFilters={applyClearAndUpdateFilters}
				locations={locations}
				targetCollectionName={TC_NAME}
				rightActions={
					<div className="flex items-center gap-2">
						{exportCsvButton}
						{addLocationButton}
					</div>
				}
			/>
		),
		[
			filterConfig,
			applyLocationFilters,
			applyClearAndUpdateFilters,
			locations,
			exportCsvButton,
			addLocationButton,
		]
	);

	return (
		<div className="locationsIndexPage overflow-x-hidden">
			<BuyerRoleUpdateForm
				wrappedComponentRef={saveFormRef('roleUpdateForm')}
				visible={roleUpdateVisible}
				formData={userContact}
				onCancel={handleBuyerRoleUpdateCancel}
				onSubmit={handleBuyerRoleUpdateSubmit}
				isLocationChange={true}
			/>

			<LocationsMapView
				fullscreen
				open={mapFullscreenOpen}
				onClose={() => setMapFullscreenOpen(false)}
				locations={inlineMapLocations}
				popupRenderFunc={popupRenderFunc}
				fetchLocationDetails={fetchLocationDetailsForMap}
				longitudeAccessor={longitudeForMap}
				latitudeAccessor={latitudeForMap}
				detailPathForRecord={detailPathForRecord}
				contactGroupsAccessor={mapContactGroupsAccessor}
				toolbar={locationsFullscreenToolbar}
			/>
			<LocationsReduxDataTable
				viewMode={viewMode}
				applyFilters={applyLocationFilters}
				clearAndUpdateFilters={applyClearAndUpdateFilters}
				mapContentLoading={inlineMapLoading}
				mapContent={
					<LocationsMapView
						locations={inlineMapLocations}
						popupRenderFunc={popupRenderFunc}
						fetchLocationDetails={fetchLocationDetailsForMap}
						longitudeAccessor={longitudeForMap}
						latitudeAccessor={latitudeForMap}
						detailPathForRecord={detailPathForRecord}
						onExpand={() => setMapFullscreenOpen(true)}
					/>
				}
				filterConfig={filterConfig}
				rightActions={locationsTableRightActions}
				handleQueryParams
				emptyState={
					<EmptyState
						graphic={
							<img
								alt="Looks like your team has not added any locations yet."
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
							/>
						}
						headline={"It's a little lonely in here."}
						body={
							currentUser.roles.some((role) => BUYER_ONLY_NO_STORE_ASSOCIATE.has(role)) ? (
								<div style={{ textAlign: 'center' }}>
									<div style={{ maxWidth: 440, marginBottom: 16 }}>
										Looks like your team has not added any locations yet.
									</div>
									<OWButtonWithSupplierManagedLock
										type="primary"
										onClick={() => history.push('/buyer/locations/overview/new')}
									>
										Add your first location
									</OWButtonWithSupplierManagedLock>
								</div>
							) : (
								<div style={{ textAlign: 'center' }}>
									<div style={{ maxWidth: 440, marginBottom: 16 }}>
										Looks like your team has not setup access for you yet.
									</div>
									<Button type="primary" onClick={showRoleUpdateModal(currentUser)}>
										Request access to my location
									</Button>
								</div>
							)
						}
					/>
				}
				entityCollectionName="locations"
				targetCollectionName={TC_NAME}
				tableColumns={columns}
				showHeader={false}
				keyAccessor={(el) => el.id}
				onTableRow={onRow}
				preAppliedFilters={{ ...INITIAL_IS_ACTIVE_FILTERS }}
				fetchData={locationsRestCrudThunksForBuyer}
				stateSlice={locations}
			/>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	locations: state.locations,
	currentUser: state.session.currentUser,
	locationTypesAdditionalFields: state.location_types_additional_fields,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	setLocationFiltersInStore: (filters) =>
		dispatch(
			LOCATIONS_CRUD_ACTION_CREATORS.updateFilters(filters, LOCATIONS_OVERVIEW_TARGET_COLLECTION)
		),
	refetchLocationsWithFilters: (filters) =>
		dispatch(
			updateFiltersEntities(ROLE_TYPES.BUYER, 'locations', locationsRestCrudThunksForBuyer, null)(
				true,
				ownProps.history,
				LOCATIONS_OVERVIEW_TARGET_COLLECTION,
				filters,
				undefined
			)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(LOCATIONS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)),
	roleUpdateUser: (entity) => dispatch(buyerRoleUpdateUser(entity)),
	fetchMultipleLocationTypesAdditionalFields: (ids, targetCollectionName) =>
		dispatch(
			locationTypesAdditionalFieldsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchLocationTypesAdditionalFields: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			locationTypesAdditionalFieldsRestCrudThunksForBuyer.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	exportLocations: (params, filters) => dispatch(downloadLocationsCSVForBuyer(params, filters)),
	fetchBrandsCount: (params) => dispatch(brandsRestCrudThunksForBuyer.countBy(params, 'all')),
	fetchFilteredLocationsLite: (filters) =>
		dispatch(
			locationsRestCrudThunksForBuyer.readLite(
				{ no_pagination: true },
				'__buyerLocationsInlineMap__',
				null,
				null,
				filters,
				false,
				true
			)
		),
	fetchLocationDetails: (id) => dispatch(fetchLocationByIdForBuyer(id)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(LocationsIndexPage));
